import { i18n } from '@lumora/common';
import React, { Component, ReactNode } from 'react';

import { errorHandler, ErrorInfo } from '../../utils/errorHandler';

import { Button } from './Button';
import { Card } from './Card';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  errorId?: string;
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  showDetails?: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });

    // Log error using our error handler
    const errorDetails: ErrorInfo = {
      code: 'REACT_ERROR_BOUNDARY',
      message: error.message,
      context: {
        componentStack: errorInfo.componentStack,
        errorBoundary: true,
        errorStack: error.stack,
      },
      recoverable: true,
      timestamp: new Date().toISOString(),
    };

    errorHandler.handleError(errorDetails, {
      logError: true,
      showToast: false, // Don't show toast for boundary errors
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="max-w-lg w-full" padding="lg">
            <div className="text-center">
              {/* Error Icon */}
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>

              {/* Error Title */}
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {i18n.t('errors.errorBoundaryTitle') ||
                  'Oops! Something went wrong'}
              </h2>

              {/* Error Message */}
              <p className="text-gray-600 mb-6">
                {i18n.t('errors.errorBoundaryMessage') ||
                  "We encountered an unexpected error. Don't worry, your data is safe. You can try refreshing the page or go back to the dashboard."}
              </p>

              {/* Error Details (Development only) */}
              {this.props.showDetails && this.state.error && (
                <details className="mb-6 text-left">
                  <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                    {i18n.t('errors.errorBoundaryTechnicalDetails') ||
                      'Technical Details'}
                  </summary>
                  <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono text-gray-700 overflow-auto max-h-32">
                    <div className="mb-2">
                      <strong>
                        {i18n.t('errors.errorBoundaryErrorLabel') || 'Error:'}
                      </strong>{' '}
                      {this.state.error.message}
                    </div>
                    {this.state.errorInfo?.componentStack && (
                      <div>
                        <strong>
                          {i18n.t('errors.errorBoundaryComponentStackLabel') ||
                            'Component Stack:'}
                        </strong>
                        <pre className="whitespace-pre-wrap">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}

              {/* Recovery Actions */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={this.handleRetry} variant="outline">
                  {i18n.t('errors.errorBoundaryTryAgain') || 'Try Again'}
                </Button>
                <Button onClick={this.handleReload} variant="outline">
                  {i18n.t('errors.errorBoundaryRefreshPage') || 'Refresh Page'}
                </Button>
                <Button onClick={this.handleGoHome}>
                  {i18n.t('errors.errorBoundaryGoToDashboard') ||
                    'Go to Dashboard'}
                </Button>
              </div>

              {this.state.errorId && (
                <p className="text-xs text-gray-400 mt-4">
                  {i18n.t('errors.errorBoundaryErrorIdLabel') || 'Error ID:'}{' '}
                  {this.state.errorId}
                </p>
              )}
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
